import { Connection, PublicKey } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, AccountLayout, getAssociatedTokenAddress } from '@solana/spl-token';

export interface TokenBalance {
  mint: string;
  balance: bigint;
  decimals: number;
  uiAmount: number;
  ata: string;
}

const cache = new Map<string, { data: TokenBalance[]; timestamp: number }>();
const TTL = 10000; // 10 seconds

export async function getUserTokenBalances(
  connection: Connection,
  walletPublicKey: PublicKey
): Promise<TokenBalance[]> {
  const key = walletPublicKey.toString();
  const now = Date.now();

  if (cache.has(key)) {
    const entry = cache.get(key)!;
    if (now - entry.timestamp < TTL) {
      return entry.data;
    }
  }

  try {
    const accounts = await connection.getTokenAccountsByOwner(walletPublicKey, {
      programId: TOKEN_PROGRAM_ID,
    });
    const balances: TokenBalance[] = [];

    for (const { account, pubkey } of accounts.value) {
      const parsed = parseTokenAccountData(account.data);
      if (parsed) {
        balances.push({
          ...parsed,
          ata: pubkey.toString(),
        });
      }
    }

    cache.set(key, { data: balances, timestamp: now });
    return balances;
  } catch (error) {
    console.error('Error fetching user token balances:', error);
    return [];
  }
}

export async function getTokenBalance(
  connection: Connection,
  walletPublicKey: PublicKey,
  mintAddress: PublicKey
): Promise<TokenBalance | null> {
  try {
    const ata = await getAssociatedTokenAddress(mintAddress, walletPublicKey);
    const accountInfo = await connection.getAccountInfo(ata);
    if (!accountInfo) return null;

    const parsed = parseTokenAccountData(accountInfo.data);
    if (!parsed) return null;

    return {
      ...parsed,
      ata: ata.toString(),
    };
  } catch (error) {
    console.error('Error fetching token balance:', error);
    return null;
  }
}

export function parseTokenAccountData(accountData: Buffer): Omit<TokenBalance, 'ata'> | null {
  try {
    const accountInfo = AccountLayout.decode(accountData);
    const mint = new PublicKey(accountInfo.mint).toString();
    const balance = accountInfo.amount;
    const decimals = accountInfo.decimals;
    const uiAmount = Number(balance) / Math.pow(10, decimals);

    return { mint, balance, decimals, uiAmount };
  } catch (error) {
    console.error('Error parsing token account data:', error);
    return null;
  }
}

export async function batchFetchTokenBalances(
  connection: Connection,
  walletPublicKey: PublicKey,
  mintAddresses: PublicKey[]
): Promise<TokenBalance[]> {
  const balances: TokenBalance[] = [];

  for (const mint of mintAddresses) {
    const balance = await getTokenBalance(connection, walletPublicKey, mint);
    if (balance) balances.push(balance);
  }

  return balances;
}